import { z } from 'zod';
import {
  FACADES,
  LAND_USES,
  MEMBERSHIP_TIER_KEYS,
  PROPERTY_PURPOSES,
  PROPERTY_TYPES,
  RENT_PERIODS,
  SIGNUP_KINDS,
  TENANT_TYPES,
} from './status.js';
import { normalizeSaudiMobile } from './phone.js';

export const saudiMobile = z
  .string()
  .transform((v, ctx) => {
    const n = normalizeSaudiMobile(v);
    if (!n) {
      // `params.rule` is what the API forwards to clients (routes/util.ts ruleOf); `message` is for humans reading it raw.
      ctx.addIssue({ code: 'custom', message: 'not a Saudi mobile number', params: { rule: { kind: 'invalid' } } });
      return z.NEVER;
    }
    return n;
  });

export const latLng = z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) });

export const propertyFeatures = z
  .object({
    furnished: z.boolean().optional(),
    kitchen: z.boolean().optional(),
    ac: z.enum(['split', 'window', 'central', 'none']).optional(),
    maidRoom: z.boolean().optional(),
    driverRoom: z.boolean().optional(),
    elevator: z.boolean().optional(),
    pool: z.boolean().optional(),
    basement: z.boolean().optional(),
    garage: z.boolean().optional(),
    duplex: z.boolean().optional(),
    waterTank: z.boolean().optional(),
    water: z.boolean().optional(),
    electricity: z.boolean().optional(),
    drainage: z.boolean().optional(),
  })
  .strict();

/**
 * How many photos a property may carry. Exported because a form that lets someone add more than this
 * only finds out when the save is refused — which is what happened: the panel took twenty, the schema
 * took ten, and the broker got a validation error naming no field.
 */
export const MAX_LISTING_PHOTOS = 10;

export const propertyInput = z
  .object({
    purpose: z.enum(PROPERTY_PURPOSES),
    type: z.enum(PROPERTY_TYPES),
    city: z.string().min(1).max(80),
    district: z.string().min(1).max(120),
    street: z.string().max(160).optional(),
    location: latLng,
    areaM2: z.number().positive().max(10_000_000),
    bedrooms: z.number().int().min(0).max(50).optional(),
    bathrooms: z.number().int().min(0).max(50).optional(),
    livingRooms: z.number().int().min(0).max(50).optional(),
    floors: z.number().int().min(0).max(200).optional(),
    floorNo: z.number().int().min(-5).max(200).optional(),
    units: z.number().int().min(0).max(5000).optional(),
    shops: z.number().int().min(0).max(5000).optional(),
    landUse: z.enum(LAND_USES).optional(),
    ageYears: z.number().int().min(0).max(200).optional(),
    facade: z.enum(FACADES).optional(),
    streetWidthM: z.number().min(0).max(200).optional(),
    features: propertyFeatures.optional(),
    rentPeriod: z.enum(RENT_PERIODS).optional(),
    tenantType: z.enum(TENANT_TYPES).optional(),
    /** The owner's own estimate, in halalas. Never shown as a Wasity valuation. */
    ownerEstimateHalalas: z.number().int().min(0).optional(),
    photoMediaIds: z.array(z.string().uuid()).max(MAX_LISTING_PHOTOS).optional(),
    notes: z.string().max(2000).optional(),
  })
  .superRefine((p, ctx) => {
    const residential = ['apartment', 'villa', 'floor', 'rest_house', 'chalet'];
    if (residential.includes(p.type)) {
      for (const k of ['bedrooms', 'bathrooms', 'livingRooms'] as const) {
        if (p[k] === undefined) ctx.addIssue({ code: 'custom', path: [k], message: 'required', params: { rule: { kind: 'required' } } });
      }
    }
    if (p.type === 'building' && p.units === undefined)
      ctx.addIssue({ code: 'custom', path: ['units'], message: 'required', params: { rule: { kind: 'required' } } });
    if (p.type === 'land' && !p.landUse)
      ctx.addIssue({ code: 'custom', path: ['landUse'], message: 'required', params: { rule: { kind: 'required' } } });
    if (p.purpose === 'rent' && !p.rentPeriod)
      ctx.addIssue({ code: 'custom', path: ['rentPeriod'], message: 'required', params: { rule: { kind: 'required' } } });
  });
export type PropertyInput = z.infer<typeof propertyInput>;

export const brokerRequestCreate = z.object({ property: propertyInput, acceptTerms: z.literal(true) });
export const evaluationCreate = z.object({
  type: z.enum(PROPERTY_TYPES),
  city: z.string().min(1).max(80),
  district: z.string().min(1).max(120),
  location: latLng,
  areaM2: z.number().positive().max(10_000_000),
  deedMediaId: z.string().uuid().optional(),
  notes: z.string().max(2000).optional(),
});
export const offerCreate = z.object({
  amountHalalas: z.number().int().positive(),
  terms: z.string().max(2000).optional(),
});
export const brokerUpdatePost = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('listed'), platform: z.string().min(1).max(60), url: z.string().url().optional() }),
  z.object({ kind: z.literal('note'), text: z.string().min(1).max(2000) }),
  z.object({ kind: z.literal('contract_signed') }),
  z.object({ kind: z.literal('licensed'), licenceNo: z.string().min(1).max(60).optional() }),
]);
export const viewingCreate = z.object({
  listingId: z.string().uuid(),
  slotStart: z.string().datetime(),
  note: z.string().max(500).optional(),
});
export const viewingRespond = z.discriminatedUnion('action', [
  z.object({ action: z.literal('accept') }),
  z.object({ action: z.literal('decline'), reason: z.string().max(500).optional() }),
  z.object({ action: z.literal('propose'), slotStart: z.string().datetime() }),
]);
/** A wall-clock `HH:MM` on a 24-hour day — `25:99` is not a time, and Postgres `time` refuses it. */
const clockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM between 00:00 and 23:59');
const toMinutes = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
/** One viewing window the owner keeps open. A window that ends before it starts yields no slots at all. */
export const availabilitySlot = z
  .object({
    weekday: z.number().int().min(0).max(6),
    start: clockTime,
    end: clockTime,
  })
  .refine((s) => toMinutes(s.end) > toMinutes(s.start), { path: ['end'], message: 'end_must_be_after_start' });
export const phoneStart = z.object({ phone: saudiMobile, locale: z.enum(['ar', 'en']).optional() });
export const phoneVerify = z.object({
  phone: saudiMobile,
  code: z.string().regex(/^\d{4,8}$/),
  name: z.string().min(2).max(80).optional(),
});
export const membershipTierInput = z.object({
  nameAr: z.string().min(1).max(60),
  nameEn: z.string().min(1).max(60),
  priceHalalas: z.number().int().min(0),
  slots: z.number().int().min(1).max(500),
  active: z.boolean().optional(),
});
/** Today in Riyadh (UTC+3) as `YYYY-MM-DD`: a licence that runs out today is still a valid licence. */
const riyadhToday = () => new Date(Date.now() + 3 * 3_600_000).toISOString().slice(0, 10);

export const signupRequestInput = z.object({
  kind: z.enum(SIGNUP_KINDS),
  crNumber: z.string().min(5).max(30),
  name: z.string().min(2).max(120),
  licenceNo: z.string().min(3).max(60),
  licenceExpiry: z.string().date().refine((d) => d >= riyadhToday(), 'licence_already_expired'),
  phone: saudiMobile,
  email: z.string().email(),
  documentMediaId: z.string().uuid().optional(),
});
export const tierKey = z.enum(MEMBERSHIP_TIER_KEYS);
